import React, { useEffect, useState } from "react";
import { SMLogo, BallGrid } from "@/components/UIBits";
import { T, fmt } from "@/lib/copy";
import { playLine } from "@/lib/audio";
import { api, getStatePref, setStatePref } from "@/lib/api";
import { Share2, RotateCcw, Zap, Trophy, Signpost } from "lucide-react";

// SUMMARY (V1) — reveal card removed, close-line auto-plays, no hear buttons.
const RESULT_COLOR = { won: "var(--sm-blaze)", level: "var(--sm-chalk)", short: "var(--sm-drs-1)", allout: "var(--sm-drs-1)" };

export default function Summary({ lang, summary, persona, overId, onPlayAgain, onShare, onPlayEliminator, challenger }) {
  const t = T(lang);
  const color = RESULT_COLOR[summary.result] || "var(--sm-drs-1)";
  const resultWord = t[summary.result] || t.short;
  const opp = summary.opponent_total != null ? summary.opponent_total : summary.chase - 1;

  useEffect(() => {
    if (summary && summary.close) {
      const id = setTimeout(() => { playLine({ text: summary.close, persona, lang }); }, 500);
      return () => clearTimeout(id);
    }
    // eslint-disable-next-line
  }, [summary && summary.close]);


  return (
    <div className="dsummary" data-testid="summary-screen">
      <div className="over-complete">{t.overComplete}</div>

      <div style={{ textAlign: "center", marginTop: 6 }}>
        <div className="result-headline" style={{ color, textShadow: summary.result === "won" ? "0 0 28px rgba(255,154,61,0.5)" : "none" }} data-testid="summary-result">{resultWord}</div>
        <div className="result-sub">{fmt(t.targetVs, { n: summary.chase, o: opp })}</div>
        {summary.result === "level" && (
          <div className="font-reading" style={{ fontSize: 12, fontStyle: "italic", color: "var(--sm-blaze)", marginTop: 3 }} data-testid="summary-tie-line">{fmt(t.tiedAt, { s: summary.score })}</div>
        )}
      </div>

      {summary.eliminator_available && !summary.eliminator && (
        <button className="btn-primary" style={{ width: "100%", padding: "15px", marginTop: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }} onClick={onPlayEliminator} data-testid="summary-play-eliminator">
          <Zap size={18} /> {t.elimPlay}
        </button>
      )}
      {summary.eliminator && (
        <div className="panel anim-fade" style={{ marginTop: 16, padding: 12, textAlign: "center", borderColor: summary.eliminator.status === "won" ? "rgba(255,176,102,0.5)" : "rgba(159,192,224,0.35)" }} data-testid="summary-eliminator-result">
          <div className="font-score up" style={{ fontSize: 14, color: summary.eliminator.status === "won" ? "var(--sm-blaze)" : "var(--sm-drs-1)" }}>
            {summary.eliminator.status === "won" ? t.elimWon : t.elimLost}
          </div>
          <div className="font-mono up" style={{ fontSize: 10, letterSpacing: "0.14em", marginTop: 4, color: "rgba(244,239,230,0.5)" }}>
            SUDDEN DEATH · {summary.eliminator.rounds} {summary.eliminator.rounds === 1 ? t.elimRound : t.elimRound + "S"}
          </div>
        </div>
      )}

      <div style={{ textAlign: "center", marginTop: 22 }}>
        <div className="score-big tnum" data-testid="summary-score">
          {summary.score}<span style={{ color: "rgba(244,239,230,0.5)", fontSize: 34 }}>/{summary.wickets}</span>
        </div>
        <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}><BallGrid grid={summary.grid} testid="summary-ball-grid" /></div>
        <div className="scorecard-cap" data-testid="summary-scorecard-label">{t.yourScorecard}</div>
      </div>

      {challenger && (
        <div className="panel anim-fade" style={{ marginTop: 18, padding: 12, textAlign: "center" }} data-testid="summary-head-to-head">
          <div className="font-score up" style={{ fontSize: 14, color: "var(--sm-blaze)" }}>HEAD TO HEAD</div>
          <div className="font-reading" style={{ fontSize: 14, marginTop: 4, color: "var(--sm-chalk)" }}>
            {fmt(t.youVs, { a: summary.score, name: challenger.challenger_name, b: challenger.challenger_score })}
            {summary.score > challenger.challenger_score ? <Trophy size={13} className="inline ml-1" style={{ color: "var(--sm-blaze)", verticalAlign: "middle" }} /> : null}
          </div>
        </div>
      )}

      <div className="streak-chip" style={{ marginTop: 18 }} data-testid="summary-streak">
        <span className="flame" aria-hidden="true" />
        <span className="streak-num tnum" data-testid="summary-streak-count">{summary.streak}</span>
        <span className="streak-cap">{t.dayStreak} · KEEP IT ALIVE</span>
      </div>

      <div className="close-wrap" data-testid="summary-maidaan-close">
        <div className="close-sign">— THE MAIDAAN</div>
        <div className="close-line">{summary.close}</div>
        <div className="close-fresh">{t.freshTomorrow}</div>
        <div style={{ marginTop: 10, textAlign: "center" }} data-testid="summary-brand-block">
          <SMLogo height={44} opacity={0.9} />
        </div>
      </div>

      <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 12 }}>
        <button className="btn-primary" style={{ width: "100%", padding: "16px", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }} onClick={onShare} data-testid="summary-share-button">
          <Share2 size={18} /> {t.share}
        </button>
        <button className="btn-secondary" style={{ width: "100%", padding: "13px", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }} onClick={onPlayAgain} data-testid="summary-play-again-button">
          <RotateCcw size={16} /> {t.playAgain}
        </button>
      </div>

      {persona === "aspiring" && <PathwayNote overId={overId} persona={persona} lang={lang} />}
    </div>
  );
}

function StateChip({ active, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="px-2.5 py-1 rounded-full font-mono text-[10px] up tracking-[0.08em]"
      style={{
        border: "1px solid " + (active ? "var(--sm-blaze)" : "var(--stroke-subtle)"),
        background: active ? "rgba(232,132,44,0.16)" : "transparent",
        color: active ? "var(--sm-blaze)" : "rgba(244,239,230,0.7)",
        transition: "background-color .18s ease, border-color .18s ease, color .18s ease",
      }}>
      {label}
    </button>
  );
}

// "From the Maidaan — your next step": one VERIFIED, source-cited pathway nugget for the Aspiring persona.
function PathwayNote({ overId, persona, lang }) {
  const t = T(lang);
  const [state, setState] = useState(() => getStatePref());
  const [states, setStates] = useState([]);
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.pathwayStates().then((d) => { if (alive) setStates(d.states || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.pathway(overId, state, lang)
      .then((d) => { if (alive) setNote(d && d.note ? d : null); })
      .catch(() => { if (alive) setNote(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [overId, state, lang]);

  function pick(code) { const next = state === code ? "" : code; setState(next); setStatePref(next); }

  return (
    <div className="panel p-4 mt-5 anim-fade" data-testid="pathway-note">
      <div className="flex items-center gap-2 mb-2">
        <Signpost size={15} style={{ color: "var(--sm-blaze)" }} />
        <span className="font-score italic up text-sm" style={{ color: "var(--sm-blaze)" }}>
          {lang === "hi" ? "Maidaan se · tumhara agla kadam" : "From the Maidaan · your next step"}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="pathway-state-chips">
        <StateChip active={!state} label={lang === "hi" ? "Poora Bharat" : "All India"} onClick={() => pick("")} testid="pathway-state-all" />
        {states.map((s) => (
          <StateChip key={s.code} active={state === s.code} label={s.name} onClick={() => pick(s.code)} testid={`pathway-state-${s.code}`} />
        ))}
      </div>
      {loading ? (
        <div className="rounded-[10px]" style={{ height: 56, background: "rgba(244,239,230,0.06)" }} data-testid="pathway-loading" />
      ) : note ? (
        <>
          <div className="font-mono text-[10px] up tracking-[0.14em] mb-1" style={{ color: "rgba(255,176,102,0.85)" }} data-testid="pathway-note-title">{note.title}</div>
          <p className="font-reading text-[13px] leading-relaxed" style={{ color: "rgba(244,239,230,0.9)" }} data-testid="pathway-note-text">{note.note}</p>
          <div className="flex items-center justify-between gap-3 mt-3">
            <span className="font-mono text-[9px] up tracking-[0.1em] leading-snug" style={{ color: "rgba(244,239,230,0.45)" }} data-testid="pathway-note-source">SOURCE · {note.source}</span>
          </div>
        </>
      ) : null}
    </div>
  );
}
